import { AppContext } from "App";

import { isVersionOlderOrEqual } from "Global/Base/Utils/versionUtils";
import { EventsController } from "Global/Dom/EventsController";
import { IDisposable } from "../../../../Global/Base/IDisposable";
import { StringUtils } from "../../../../Global/Base/Utils/StringUtils";
import { Localization } from "../../../../OW.Remoting/Remoting/Localization";
import { DropDownMenu } from "../../../UI.Controls/DropDownMenu/DropDownMenu";
import { DropDownMenuCopyFormLinkItem } from "../../../UI.Controls/DropDownMenu/DropDownMenuCopyFormLinkItem";
import { DropDownMenuItem } from "../../../UI.Controls/DropDownMenu/DropDownMenuItem";
import { SeparatorMenuItem } from "../../../UI.Controls/DropDownMenu/SeparatorItem";
import { SeparatorMenuItemWithText } from "../../../UI.Controls/DropDownMenu/SeparatorItemWithText";
import { SplitPanel } from "../../../UI.ElementarControls/SplitPanel";
import { createElement } from "../../../UI.ElementarControls/Utils/CustomElementUtils";
import { IHTMLElementWithControl } from "../Controls/ControlInterfaces";
import { IContentBuilder } from "../DataBuilders/IContentBuilder";
import { FormManager } from "../FormManager/IFormManager";
import { FormManagerProperty } from "../FormManager/IFormManagerInterfaces";
import { Icon } from "../FormUI/FormUI";
import { MessageType, MessageWindow } from "../MessageWindow";
import { WindowState } from "../UIManagerInterfaces";
import { DropDownMenuFormProfileItem } from "./Filters/DropDownMenuItems/DropDownMenuFormProfileItem";
import { Form } from "./Form";
import { FormProfileItem } from "./Forms/FormProfileItem";
import { IDetailMasterableFrame } from "./Frames/IDetailMasterableFrame";
import { TreeViewFrame } from "./Frames/TreeViewFrame";

/**
 * Popisek k formuláři
 */
export class FormMenu implements IDisposable {
    //#region Fields
    public element: HTMLElement;

    private _settingsButton: HTMLElement;
    private _switchLayoutButton: HTMLElement;
    private _addFavorite: HTMLElement;
    private _removeFavorite: HTMLElement;

    private _subMenu: DropDownMenu;
    private readonly _ownerForm: Form;
    private readonly _formMenuStyleClass: string = "current-form-menu";
    private _eventsController: EventsController = new EventsController();
    private _copyFormLinkDropDownMenuItem: DropDownMenuCopyFormLinkItem;
    //#endregion Fields

    //#region Properties

    public set visible(v: boolean) {
        this.element.style.display = v ? "" : "none";
    }
    public get visible(): boolean {
        return this.element.style.display != "none";
    }

    /**
     * Poslední položka v menu
     */
    private get _lastFormCaptionMenuItem(): DropDownMenuItem {
        const profiles = this._subMenu.querySelectorAll(".form-profile-item");
        return profiles.length > 0 ? profiles[profiles.length - 1] as DropDownMenuItem : undefined;
    }
    //#endregion Properties

    //#region Constructors
    constructor(ownerForm: Form) {
        this._ownerForm = ownerForm;

        this._createMenu();
    }
    //#endregion Constructors

    //#region Methods
    /**
    * Vytvoření menu
    */
    private _createMenu(): void {
        this.element = createElement({ classes: this._formMenuStyleClass });
        this.element.style.display = "none";

        if (!this._ownerForm.isModal && StringUtils.isNullOrEmpty(this._ownerForm.formCondition) && !this._ownerForm.visibleOnlyDetail && this._ownerForm.menuItemId) {
            this._addFavorite = this._createButton(Icon[Icon.favorite], "GUI-1006"); // Přidat do oblíbených
            this._removeFavorite = this._createButton("ui-star-filled", "GUI-508345", false); // Odebrat z oblíbených

            this._switchFavoriteButtons(this._ownerForm.getState(WindowState.favorite));
            this._eventsController.add(this._ownerForm.stateChanged, (_, prm) => {
                if (prm.state == WindowState.favorite)
                    this._switchFavoriteButtons(prm.value);
            });
        }

        if (this._checkIfLayoutChangeButtonVisible()) {
            this._switchLayoutButton = this._createButton(Icon[Icon.layoutChange], "GUI-415252"); // Přepnout roložení
        }

        this._settingsButton = this._createButton(Icon[Icon.settings], "GUI-583314"); // Nastavení formuláře

        this._setListeners();
    }

    /**
    * Vytvoření tlačítka s ikonou
    */
    private _createButton(icon: string, locKey: string, visible: boolean = true): HTMLElement {
        const button = createElement({ type: "button", classes: ["topBarButton", "btn", "flat", this._formMenuStyleClass + "__item"], attrs: { title: locKey, "data-lkd": locKey } });
        Localization.addLocData(button, locKey, "title");
        const iconElement = createElement({ type: "div", classes: ["icon", icon], attrs: { "data-lkd": locKey } });

        button.appendChild(iconElement);
        button.style.display = visible ? "" : "none";

        this.element.appendChild(button);
        return button;
    }

    private _setListeners(): void {
        if (this._settingsButton) {
            this._eventsController.addListener(this._settingsButton, "click", (e: PointerEvent) => {
                if (this._settingsButton.classList.contains("Disabled"))
                    return;
                if (!this._subMenu)
                    this._createSubMenu(); // vytvoří se až když je potřeba
                if (this._subMenu.isOpened) {
                    this._subMenu.close();
                } else {
                    this._subMenu.show(this._settingsButton, e, e.detail === 0); // detail === 0 je v eventu nastaven, pokud přijde z klávesnice
                }
            });
        }
        if (this._switchLayoutButton) {
            this._eventsController.addListener(this._switchLayoutButton, "click", () => {
                if (this._switchLayoutButton.classList.contains("Disabled"))
                    return;
                this._ownerForm.toggleLayout();
            });
        }

        if (this._addFavorite) {
            this._eventsController.addListener(this._addFavorite, "click", () => {
                if (this._addFavorite.classList.contains("Disabled"))
                    return;
                if (this._ownerForm.menuItemId)
                    FormManager.instance.setProperty(FormManagerProperty.favoriteForm, { menuId: this._ownerForm.menuItemId, formName: this._ownerForm.formName, value: true, profile: this._ownerForm.profileManager?.selectedProfile });
                else
                    throw new Error("[FormMenu] Cannot set favorite form, because owner form has undefined menu id.");
            });
        }

        if (this._removeFavorite) {
            this._eventsController.addListener(this._removeFavorite, "click", () => {
                if (this._removeFavorite.classList.contains("Disabled"))
                    return;
                if (this._ownerForm.menuItemId)
                    FormManager.instance.setProperty(FormManagerProperty.favoriteForm, { menuId: this._ownerForm.menuItemId, formName: this._ownerForm.formName, value: false, profile: this._ownerForm.profileManager?.selectedProfile });
                else
                    throw new Error("[FormMenu] Cannot unset favorite form, because owner form has undefined menu id.");
            });
        }
    }

    /**
     * Vytvoří submenu na tlačítku Nastavení formuláře
     */
    private _createSubMenu(): void {
        this._subMenu = new DropDownMenu();

        // U modálů a parametrických formulářů nechceme tyto akce v současné době zobrazovat. V případě prvního nedávají moc smysl a v případě druhého chybí mechanismus pro přenos potřebných informací
        if (!this._ownerForm.isModal && StringUtils.isNullOrEmpty(this._ownerForm.formCondition) && !this._ownerForm.visibleOnlyDetail) {
            this._copyFormLinkDropDownMenuItem = new DropDownMenuCopyFormLinkItem();
            this._copyFormLinkDropDownMenuItem.ownerForm = this._ownerForm;
            this._copyFormLinkDropDownMenuItem.locKey = "GUI-650161"; // Kopírovat adresu formuláře
            this._copyFormLinkDropDownMenuItem.icon = "web-link";
            this._subMenu.addItem(this._copyFormLinkDropDownMenuItem);
        }

        this._createProfileMenu(this._subMenu);

        this._subMenu.addItem(new SeparatorMenuItem());
        this._subMenu.addItem(DropDownMenuItem.createDropMenuItem(Icon[Icon.info], async () => {
            return this._showFormInfo();
        }, "GUI-583311", true, 3)); // Informace o formuláři

        if (AppContext.instance.configuration.developmentMode) {
            this._subMenu.addItem(DropDownMenuItem.createDropMenuItem("web-code", async () => this._showFormDefinition(), "GUI-642521", true, 3)); // Definice formuláře
        }
    }

    private _createProfileMenu(menu: DropDownMenu): void {
        menu.addItem(SeparatorMenuItemWithText.createSeparatorWithText("GUI-623211")); // Zobrazení

        // profily
        for (const profileItem of this._ownerForm.profileManager.profiles) {
            const dropdownMenuItem = this._addCaptionMenuItem(menu, profileItem);
            profileItem.setFormDropDownMenuItem(dropdownMenuItem);
        }

        menu.addItem(DropDownMenuItem.createDropMenuItem(Icon[Icon.settingsEqualizerB], async () => {
            this._ownerForm.showProfilesPanel();
        }, "GUI-623204", true, 3)); // Správa zobrazení

        // přidání položky při vzniku nového profilu
        this._ownerForm.profileManager.profileAdded.add((_mgr, newFormProfileItem) => {
            const newDropdownMenuItem = this._addCaptionMenuItem(menu, newFormProfileItem);
            newFormProfileItem.setFormDropDownMenuItem(newDropdownMenuItem);
        });
    }

    /**
     * Vytvoří položku v submenu formuláře a prováže události
     * @param profile - položka profilu formuláře
     */
    private _addCaptionMenuItem(menu: DropDownMenu, profile: FormProfileItem): DropDownMenuFormProfileItem {
        const menuItem = DropDownMenuFormProfileItem.createDropDownMenuFormProfileItem(Icon[Icon.none], async () => {
            profile.selected = true;
        }, profile.id, profile.title, profile.locKey, profile.default, profile.isImplementation, this._ownerForm.profileManager.canEditImplementationProfiles, profile.selected, true, 3);

        const last = this._lastFormCaptionMenuItem;
        last ? menu.addItemAfter(menuItem, last) : menu.addItem(menuItem);

        return menuItem;
    }

    private _switchFavoriteButtons(favorite: boolean): void {
        if (favorite) {
            this._addFavorite.style.display = "none";
            this._removeFavorite.style.display = "";
        } else {
            this._removeFavorite.style.display = "none";
            this._addFavorite.style.display = "";
        }
    }

    private _checkIfLayoutChangeButtonVisible(): boolean {
        // tlačítko Změnit rozložení se má zobrazovat pouze pokud hlavní frame má detail a je viditelný
        // nebo formulář obsahuje pouze SplitPanel a ten neobsahuje TreeViewFrame
        const mainFrame = this._ownerForm.masterableFrames.find(f => f.isMainFrame);
        const formInternalPanel = this._ownerForm.element.querySelector("div[data-tsccontrol='FormInternalPanel']");
        let spWhitoutTVF: boolean = false;
        if (formInternalPanel?.children.length === 1 && formInternalPanel?.children[0].tagName.toLowerCase() === SplitPanel.is) {
            spWhitoutTVF = !(Array.from(formInternalPanel.children[0].children).some(child => (child as IHTMLElementWithControl).tscControl instanceof TreeViewFrame));
        }
        return ((mainFrame != null && mainFrame instanceof IDetailMasterableFrame && mainFrame.detail != null && mainFrame.visible) || spWhitoutTVF);
    }

    private _showFormInfo(): void {
        const mw = new MessageWindow(new VersionInfoBuilder(this._ownerForm.formName, this._ownerForm.version));
        mw.header = "GUI-583311"; // Informace
        mw.type = MessageType.message;

        if (AppContext.instance.configuration.developmentMode) {
            mw.otherButtonCaption = "GUI-687991"; // Zobrazit HTML definici
            mw.otherCallback = async () => {
                window.open(this._createFormDefinitionUrl(this._ownerForm.formName));
                return true;
            };
        }

        mw.show();
    }

    private _createFormDefinitionUrl(formName: string): string {
        formName = formName?.toLowerCase();
        const handlerUrlPart = isVersionOlderOrEqual(AppContext.instance.configuration.getDevelopmentOption("webServiceVersion"), "15.03")
            ? `/mwclient/js/${formName}.aspx`
            : `/api/v01/as/gui/forms/${formName}/`;
        return `${AppContext.instance.addresses.serviceUrl}${handlerUrlPart}?clientType=MultiWeb`;
    }

    private async _showFormDefinition(): Promise<void> {
        FormManager.instance.openForm("FRM.CFormular.CFormular_servis", { condition: `DB.Upper(Kod_formulare) = DB.Upper('${this._ownerForm.formName}')` });
    }
    //#endregion Methods

    //#region Dispose
    public dispose(): void {
        this._eventsController.dispose();
        if (this._subMenu) this._subMenu.dispose();
        this.element.remove();
    }
    //#endregion Dispose
}

class VersionInfoBuilder implements IContentBuilder {
    private _code: string;
    private _version?: string;

    constructor(code: string, version?: string) {
        this._code = code;
        this._version = version;
    }

    public async build(): Promise<HTMLElement> {
        const blockClass = "version-info";
        const container = createElement({ classes: blockClass });

        container.appendChild(Localization.addLocData(createElement({ classes: `${blockClass}__form-code-title` }), "GUI-687956", Localization.TEXT_CONTENT)); // Kód formuláře
        container.appendChild(createElement({ classes: `${blockClass}__form-code-value`, value: this._code }));

        container.appendChild(Localization.addLocData(createElement({ classes: `${blockClass}__form-version-title` }), "GUI-687957", Localization.TEXT_CONTENT)); // Verze formuláře
        container.appendChild(createElement({ classes: `${blockClass}__form-version-value`, value: this._version || "0" }));

        return container;
    }
}
